import React, { useState, useEffect, useRef } from 'react';
import { safeFetchJSON } from '../utils/apiHelpers';
import { motion } from 'framer-motion';
import { X, Send, Bot, User } from 'lucide-react';
// 🔒 P0 EXECUTION CONTEXT: Arrêt des tâches à la sortie de page
import { usePageExecutionContext } from '../hooks/usePageExecutionContext';

import { getBackendUrl, getApiUrl } from '../config/environment';
const BACKEND_URL = getBackendUrl();

export default function ElyaChat({ onClose }) {
  // 🔒 P0 EXECUTION CONTEXT: Arrêt des tâches backend à la fermeture du chat
  usePageExecutionContext('elya-chat');
  
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "👋 Bonjour ! Je suis Elya, votre assistante IA de Coach DIA. Comment puis-je vous aider aujourd'hui ?",
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  // Generate session ID on mount
  useEffect(() => {
    const newSessionId = `elya-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setSessionId(newSessionId);
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading || !sessionId) return;

    const userMessage = {
      role: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const sentText = inputValue;
    setInputValue('');
    setIsLoading(true);

    try {
      // 🔒 safeFetchJSON injecte explicitement X-Execution-Context-Id
      // (évite la race condition avec le patch global de window.fetch)
      const data = await safeFetchJSON(`${BACKEND_URL}/api/assistant/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: sentText, session_id: sessionId })
      });

      const assistantMessage = {
        role: 'assistant',
        content: data.reply,
        timestamp: new Date(data.timestamp)
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('[Elya] Error sending message:', error);
      // Message contextualisé : explique + propose une action concrète (zéro fallback générique)
      const contextualMessage = {
        role: 'assistant',
        content:
          "Je n'ai pas pu transmettre votre question au moteur de réponse à l'instant. Voici ce que je vous propose :\n\n" +
          "• **Reformuler en 1 phrase** votre question (ex : « Comment fonctionne le mode certifiant ? »)\n" +
          "• **Réessayer dans quelques secondes** — la connexion peut être instable\n" +
          "• Si cela persiste, écrivez à **support@coachdia.com** ou consultez la page **Tarifs** pour les offres.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, contextualMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, x: 20 }}
        animate={{ scale: 1, x: 0 }}
        exit={{ scale: 0.9, x: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-gradient-to-br from-[#2A1454] to-[#1B0F3B] rounded-2xl p-6 max-w-2xl w-full h-[80vh] flex flex-col border border-[#FFD86F]/20 shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FFD86F] to-[#FFC933] flex items-center justify-center">
              <Bot className="w-6 h-6 text-[#1B0F3B]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#FFD86F] font-playfair">
                Elya
              </h2>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                <p className="text-gray-300 text-sm">En ligne</p>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
            aria-label="Fermer"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto mb-4 space-y-4 pr-2 scrollbar-thin scrollbar-thumb-[#FFD86F]/20 scrollbar-track-transparent">
          {messages.map((message, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className={`flex gap-3 ${
                message.role === 'user' ? 'justify-end' : 'justify-start'
              }`}
            >
              {message.role === 'assistant' && (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FFD86F] to-[#FFC933] flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-[#1B0F3B]" />
                </div>
              )}
              <div
                className={`max-w-[75%] rounded-2xl p-4 ${
                  message.role === 'user'
                    ? 'bg-gradient-to-r from-[#FFD86F] to-[#FFC933] text-[#1B0F3B]'
                    : 'bg-white/5 text-white border border-white/10'
                }`}
              >
                <p className="text-sm leading-relaxed whitespace-pre-wrap">
                  {message.content}
                </p>
                <p
                  className={`text-xs mt-2 ${
                    message.role === 'user' ? 'text-[#1B0F3B]/70' : 'text-gray-400'
                  }`}
                >
                  {formatTimestamp(message.timestamp)}
                </p>
              </div>
              {message.role === 'user' && (
                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-white" />
                </div>
              )}
            </motion.div>
          ))}
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex gap-3 justify-start"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FFD86F] to-[#FFC933] flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4 text-[#1B0F3B]" />
              </div>
              <div className="bg-white/5 text-white border border-white/10 rounded-2xl p-4">
                <div className="flex gap-2">
                  <div className="w-2 h-2 bg-[#FFD86F] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-[#FFD86F] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-[#FFD86F] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </motion.div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSendMessage} className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            disabled={isLoading}
            placeholder="Posez votre question à Elya..."
            className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#FFD86F] focus:ring-1 focus:ring-[#FFD86F] disabled:opacity-50 transition-all"
          />
          <button
            type="submit"
            disabled={!inputValue.trim() || isLoading}
            className="px-6 py-3 bg-gradient-to-r from-[#FFD86F] to-[#FFC933] text-[#1B0F3B] font-semibold rounded-lg hover:shadow-[0_0_20px_rgba(255,216,111,0.6)] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
          >
            <Send className="w-5 h-5" />
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
}
